'use client';

import { createContext, use, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'dark', // Default to dark for server-side rendering
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  storageKey = 'app-store-tracker-theme',
  defaultTheme = 'dark',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers
  const [mounted, setMounted] = useState(false);

  // Initialize theme from localStorage or default to dark
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(storageKey) as Theme | null;
      // oxlint-disable-next-line react-doctor/no-derived-state
      setTheme(savedTheme || defaultTheme);
    } catch {
      // oxlint-disable-next-line react-doctor/no-derived-state
      setTheme('dark'); // Fallback to dark if localStorage is not available
    }
    // SSR-safe hydration: localStorage is unreadable during server render, so theme syncs once on mount.
    // oxlint-disable-next-line react-doctor/no-adjust-state-on-prop-change
    setMounted(true);
  }, [storageKey, defaultTheme]);

  // Update the theme when it changes
  // oxlint-disable-next-line react-doctor/no-effect-chain
  useEffect(() => {
    if (!mounted) return;
    
    const root = window.document.documentElement;
    
    root.classList.remove('light', 'dark');
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
      root.style.colorScheme = systemTheme;
    } else {
      root.classList.add(theme);
      root.style.colorScheme = theme;
    }
  }, [theme, mounted]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = use(ThemeProviderContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}; 